import React, { useEffect, useRef } from "react";

// Iridescent cosmos backdrop: nebula orbs + turning sheen (CSS) and a gentle twinkling starfield (canvas).
// Stars are soft round glow sprites only (no lines). The few stars that would sit directly on the
// CREATION wordmark are skipped. Purely decorative; it never reads or writes app data.
const TINTS = ["255,255,255", "255,255,255", "255,255,255", "255,255,255", "255,255,255"]; // stars are white only
const COUNT = 140;
const BIG = 0.05;
const SIZE = 1.25;

export default function Cosmos() {
  const ref = useRef(null);

  useEffect(() => {
    const cv = ref.current;
    if (!cv) return undefined;
    const cx = cv.getContext("2d");
    const reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const sprites = {};
    const sprite = (rgb, s) => {
      const k = rgb + s;
      if (sprites[k]) return sprites[k];
      const c = document.createElement("canvas");
      c.width = c.height = s * 8;
      const g = c.getContext("2d");
      const h = s * 4;
      const gr = g.createRadialGradient(h, h, 0, h, h, h);
      gr.addColorStop(0, `rgba(${rgb},1)`);
      gr.addColorStop(0.12, `rgba(${rgb},.85)`);
      gr.addColorStop(0.3, `rgba(${rgb},.22)`);
      gr.addColorStop(1, `rgba(${rgb},0)`);
      g.fillStyle = gr;
      g.fillRect(0, 0, c.width, c.height);
      return (sprites[k] = c);
    };
    let W = 0, H = 0, stars = [], seed = 11, raf = 0, zone = null;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const rnd = () => ((seed = (seed * 16807) % 2147483647) / 2147483647);

    const updZone = () => {
      const t = document.querySelector(".creation-title");
      if (!t) { zone = null; return; }
      const r = document.createRange();
      r.selectNodeContents(t);
      const b = r.getBoundingClientRect();
      zone = { l: b.left, r: b.right, t: b.top, b: b.bottom };
    };
    const size = () => {
      W = window.innerWidth;
      H = window.innerHeight;
      cv.width = W * dpr;
      cv.height = H * dpr;
      cx.setTransform(dpr, 0, 0, dpr, 0, 0);
      seed = 11;
      const n = Math.round((COUNT * (W * H)) / Math.pow(390 * 844, 0.98));
      stars = Array.from({ length: n }, () => {
        const big = rnd() < BIG;
        const st = {
          x: rnd() * W, y: rnd() * H,
          s: (big ? 3.4 + rnd() * 2.6 : 0.7 + rnd() * 1.6) * SIZE,
          rgb: big ? TINTS[(rnd() * 4) | 0] : TINTS[(rnd() * TINTS.length) | 0],
          ph: rnd() * 6.28, sp: 0.4 + rnd() * 1.6, big, a: 0.4 + rnd() * 0.6,
        };
        // keep the top-left corner (the ··· menu) tidy: nudge any star there down/right and make it smaller
        if (st.x < 36 && st.y < 46) {
          st.x += 14;
          st.y += 16;
          st.s *= 0.55;
        }
        return st;
      });
      updZone();
    };
    const draw = (t) => {
      cx.clearRect(0, 0, W, H);
      for (const s of stars) {
        const m = s.s * 1.5;
        if (zone && s.x > zone.l - m && s.x < zone.r + m && s.y > zone.t - m && s.y < zone.b + m) continue;
        const tw = reduce ? 1 : 0.55 + 0.45 * Math.sin((t / 1000) * s.sp + s.ph);
        const d = s.s * 6;
        cx.globalAlpha = s.a * (s.big ? 0.75 + 0.25 * tw : tw);
        cx.drawImage(sprite(s.rgb, Math.max(2, Math.round(s.s))), s.x - d / 2, s.y - d / 2, d, d);
      }
      cx.globalAlpha = 1;
    };
    const frame = (t) => { draw(t); raf = requestAnimationFrame(frame); };

    size();
    const onResize = () => { size(); if (reduce) draw(0); };
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", updZone, { passive: true });
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(updZone);
    if (reduce) draw(0); else raf = requestAnimationFrame(frame);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", updZone);
    };
  }, []);

  return (
    <>
      <div className="cosmos" aria-hidden="true">
        <div className="cosmos-sheen" />
        <div className="cosmos-orb o1" />
        <div className="cosmos-orb o2" />
        <div className="cosmos-orb o3" />
        <div className="cosmos-orb o4" />
        <canvas ref={ref} />
      </div>
      <div className="cosmos-grain" aria-hidden="true" />
    </>
  );
}
